import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { PriceListItem, Subscription, SubscriptionLine } from '@/types'
import priceListService from '@/services/priceListService'
import subscriptionService from '@/services/subscriptionService'
import { mutationLockStore } from '@/services/mutationLockStore'
import { ORDER_EDITING_DISABLED } from '@/config/maintenance'
import { useAuth } from '@/hooks/useAuth'
import { getChargebeeIdForDay, getDayName } from '@/utils/deliveryDays'
import { isOneOffProduct, oneOffProductPath } from '@/utils/oneOffProduct'
import { addSchedules, billingFrequencyOf, mainSubscription } from '@/utils/billingFrequency'
import ProductDetailModal from '@/components/sun/ProductDetailModal'
import ProductBrowser from '@/components/products/ProductBrowser'
import AddProductModal from '@/components/AddProductModal'
import ChangeItemsModal from '@/components/ChangeItemsModal'
import OrderEditingDisabledModal from '@/components/OrderEditingDisabledModal'
import '@/styles/pages/products.css'

/**
 * The shop: WooCommerce's own categories down the left, product tiles on the
 * right, priced for this customer.
 *
 * Everything here comes from the same price list the add wizard uses, so a
 * product only appears if the customer can actually order it — Woo content,
 * not hidden, and priced in their bracket. Tiles they already have are marked,
 * which is what decides whether opening one offers "Add" or "Change".
 */
function Products(): React.JSX.Element {
  const { user } = useAuth()

  const [items, setItems] = useState<PriceListItem[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [viewing, setViewing] = useState<PriceListItem | null>(null)
  const [addingProductId, setAddingProductId] = useState<string | null>(null)
  const [editingProductId, setEditingProductId] = useState<string | null>(null)
  const [isEditingDisabledOpen, setIsEditingDisabledOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      // The catalogue is the price list for the subscription new products go
      // on, so everything offered matches its frequency (DF-379).
      const subs = await subscriptionService.getSubscriptions()
      const target = mainSubscription(subs)
      const list = await priceListService.getPriceListItems(billingFrequencyOf(target))
      setItems(list)
      setSubscriptions(subs)
      setError(null)
    } catch (err) {
      console.error('Failed to load products:', err)
      setError('We could not load the products just now. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  /**
   * Reload after a save. NOT the same as load().
   *
   * load() reads subscriptions with a plain GET, and that endpoint only
   * re-syncs from CRM when the local copy is more than five minutes old
   * (SYNC_TTL_MINUTES, BUG-6). Called straight after a save the copy is
   * seconds old, so the read returned the state from BEFORE the change: a
   * product added here did not appear on My Deliveries, and a browser refresh
   * did not bring it back either, because that read hit the same TTL. It only
   * turned up once some other save forced a sync, which then revealed both.
   *
   * The forced refresh polls CRM until the change has settled, which is what
   * both dashboards already do after a save.
   */
  const handleSuccess = useCallback(async () => {
    mutationLockStore.begin('Updating your deliveries...')

    try {
      setSubscriptions(await subscriptionService.refreshSubscriptionsUntilSettled())
      mutationLockStore.succeed('Saved')
    } catch (err) {
      // The change itself went through; only the refresh did not. Saying
      // "could not save" here would send them to make it twice.
      console.error('Failed to refresh after saving:', err)
      mutationLockStore.fail('Saved, but we could not refresh the page. Please reload.')
    }
  }, [])

  /** Every live line, so a tile can show whether it's already on the order.
   *  The delivery surcharge is excluded: it isn't a product the customer adds
   *  or manages (DF-88). */
  const currentLines = useMemo<SubscriptionLine[]>(
    () => subscriptions
      .filter(s => s.statecode !== 1)
      .flatMap(s => s.lines.filter(l => l.statecode !== 1 && !l.is_surcharge && !l.is_one_off)),
    [subscriptions]
  )

  /** What the customer already gets of each product, for the card's status. */
  const orderedByProduct = useMemo(() => {
    const map = new Map<string, { day: number, quantity: number }[]>()

    currentLines.forEach(line => {
      if (!line.product_id || line.delivery_day === null || line.delivery_day === undefined) return

      const days = map.get(line.product_id) ?? []
      days.push({ day: line.delivery_day, quantity: line.quantity })
      map.set(line.product_id, days)
    })

    return map
  }, [currentLines])

  const orderedProductIds = useMemo(
    () => new Set(currentLines.filter(l => !l.end_date).map(l => l.product_id)),
    [currentLines]
  )

  /** The customer's delivery days, from CRM with their lines as a fallback. */
  const deliveryDays = useMemo<number[]>(() => {
    if (user?.delivery_days && user.delivery_days.length > 0) return [...user.delivery_days]
    const days = new Set<number>()
    currentLines.forEach(l => {
      if (l.delivery_day !== null && l.delivery_day !== undefined) days.add(l.delivery_day)
    })
    return Array.from(days)
  }, [user?.delivery_days, currentLines])

  /**
   * Can this product reach this customer at all? A product is only delivered
   * on the weekdays it has a ChargeBee id for, and a handful don't cover any of
   * a given customer's round days. Saying so on the tile beats letting them
   * open it and hit a dead end in the wizard.
   */
  const isOrderable = useCallback(
    (item: PriceListItem): boolean =>
      deliveryDays.some(day => getChargebeeIdForDay(item, day) !== null),
    [deliveryDays]
  )

  // Never a CRM quotation (DF-381).
  const activeSubscription = useMemo(() => mainSubscription(subscriptions), [subscriptions])

  const navigate = useNavigate()

  const goToOneOff = useCallback(
    (item: PriceListItem) => navigate(oneOffProductPath(item)),
    [navigate]
  )

  const guardEditing = useCallback((open: () => void) => {
    if (ORDER_EDITING_DISABLED) {
      setIsEditingDisabledOpen(true)
      return
    }
    open()
  }, [])

  /** Opening a tile offers whichever action makes sense for that product. */
  const actOnViewed = useCallback(() => {
    if (!viewing) return
    const item = viewing
    setViewing(null)
    guardEditing(() => {
      if (isOneOffProduct(item)) {
        goToOneOff(item)
      } else if (orderedProductIds.has(item.product_id)) {
        setEditingProductId(item.product_id)
      } else {
        setAddingProductId(item.product_id)
      }
    })
  }, [viewing, orderedProductIds, guardEditing, goToOneOff])

  const productName = (item: PriceListItem): string =>
    item.name || item.description || item.product_number

  return (
    <div className="products-page">
      <h1 className="products-title">Products</h1>

      {loading ? (
        <p className="products-empty">Loading products...</p>
      ) : error ? (
        <div className="products-error">
          <p>{error}</p>
          <button className="sun-btn" onClick={load}>Try again</button>
        </div>
      ) : (
        <ProductBrowser
          items={items}
          orderedByProduct={orderedByProduct}
          isOrderable={isOrderable}
          actionLabel={item => (!isOneOffProduct(item) && orderedProductIds.has(item.product_id) ? 'Change' : 'Add')}
          onAction={item => guardEditing(() => {
            if (isOneOffProduct(item)) goToOneOff(item)
            else if (orderedProductIds.has(item.product_id)) setEditingProductId(item.product_id)
            else setAddingProductId(item.product_id)
          })}
          onViewDetail={setViewing}
        />
      )}

      <ProductDetailModal
        productId={viewing?.product_id ?? null}
        fallbackName={viewing ? productName(viewing) : null}
        unitPrice={viewing ? (viewing.amount_inc_vat ?? viewing.amount) : null}
        deliverableDays={viewing
          ? deliveryDays.filter(day => getChargebeeIdForDay(viewing, day) !== null)
          : undefined}
        customerDeliveryDays={deliveryDays}
        actionLabel={
          viewing && !isOneOffProduct(viewing) && orderedProductIds.has(viewing.product_id)
            ? 'Change my order'
            : 'Add to my order'
        }
        onAction={viewing && isOrderable(viewing) ? actOnViewed : undefined}
        actionNote={
          viewing && !isOrderable(viewing)
            ? `Sorry, this isn't delivered on your delivery ${deliveryDays.length === 1 ? 'day' : 'days'} (${deliveryDays.map(getDayName).join(' and ')}).`
            : undefined
        }
        onClose={() => setViewing(null)}
      />

      <AddProductModal
        isOpen={addingProductId !== null}
        onClose={() => setAddingProductId(null)}
        onSuccess={handleSuccess}
        subscriptionId={activeSubscription?.id ?? ''}
        billingFrequency={billingFrequencyOf(activeSubscription)}
        schedules={addSchedules(subscriptions)}
        currentItems={currentLines}
        initialProductId={addingProductId ?? undefined}
        initialProductSku={items.find(i => i.product_id === addingProductId)?.product_number}
        firstDeliveryLockDate={activeSubscription?.is_awaiting_first_delivery ? activeSubscription.first_delivery_date : null}
      />

      <ChangeItemsModal
        isOpen={editingProductId !== null}
        onClose={() => setEditingProductId(null)}
        onSuccess={handleSuccess}
        currentItems={currentLines}
        subscriptionId={activeSubscription?.id ?? ''}
        subscriptions={subscriptions}
        deliveryDay={new Date().getDay()}
        initialProductId={editingProductId ?? undefined}
      />

      <OrderEditingDisabledModal
        isOpen={isEditingDisabledOpen}
        onClose={() => setIsEditingDisabledOpen(false)}
      />
    </div>
  )
}

export default Products
